import {
  createContext,
  useReducer,
  ReactNode,
  Dispatch,
  useContext,
} from 'react';

import moment from 'moment';

import { VehicleOfficeSearchViewModel } from '@api/index';

import CarRentalSearchBarReducer from './reducer';
import { ICarRentalSearchBarState, initialState } from './state';
import {
  CarRentalSearchBarActions,
  CarRentalSearchBarActionType,
} from './actions';

interface ICarRentalSearchBarProvider {
  children?: ReactNode;
}

export const CarRentalSearchBarContext = createContext<{
  state: ICarRentalSearchBarState;
  dispatch: Dispatch<CarRentalSearchBarActions>;
  updateArrival: (arrival: VehicleOfficeSearchViewModel | null) => void;
  updateDeparture: (departure: VehicleOfficeSearchViewModel | null) => void;
  updatePickUpDate: (value: moment.Moment) => void;
  updateDropUpDate: (value: moment.Moment) => void;
  updatePickUpTime: (value: string) => void;
  updateDropUpTime: (value: string) => void;
  updateIsDifferentLocation: (value: boolean) => void;
}>({
  state: initialState,
  dispatch: () => null,
  updateArrival: () => null,
  updateDeparture: () => null,
  updatePickUpDate: () => null,
  updateDropUpDate: () => null,
  updatePickUpTime: () => null,
  updateDropUpTime: () => null,
  updateIsDifferentLocation: () => null,
});

export const CarRentalSearchBarProvider = ({
  children,
}: ICarRentalSearchBarProvider) => {
  const [state, dispatch] = useReducer(CarRentalSearchBarReducer, initialState);

  const updateArrival = (arrival: VehicleOfficeSearchViewModel) => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_ARRIVAL,
      payload: arrival,
    });
  };
  const updateDeparture = (departure: VehicleOfficeSearchViewModel) => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_DEPERATURE,
      payload: departure,
    });
  };
  const updatePickUpDate = (pickUpDate: moment.Moment) => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_PICK_UP_DATE,
      payload: pickUpDate,
    });
  };

  const updateDropUpDate = (dropUpDate: moment.Moment) => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_DROP_UP_DATE,
      payload: dropUpDate,
    });
  };
  const updateDropUpTime = (dropUpTime: string) => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_DROP_UP_TIME,
      payload: dropUpTime,
    });
  };
  const updatePickUpTime = (pickUpTime: string) => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_PICK_UP_TIME,
      payload: pickUpTime,
    });
  };

  const updateIsDifferentLocation = (checked: boolean) => {
    dispatch({
      type: CarRentalSearchBarActionType.SET_IS_DIFFERENT_LOCATION,
      payload: checked,
    });
  };

  return (
    <CarRentalSearchBarContext.Provider
      value={{
        state,
        dispatch,
        updateArrival,
        updateDeparture,
        updatePickUpDate,
        updateDropUpDate,
        updateDropUpTime,
        updatePickUpTime,
        updateIsDifferentLocation,
      }}
    >
      {children}
    </CarRentalSearchBarContext.Provider>
  );
};

const useCarRentalSearchBar = () => {
  const context = useContext(CarRentalSearchBarContext);

  if (context === undefined) {
    throw new Error(
      'useCarRentalSearchBar must be used within useCarRentalSearchBar'
    );
  }

  return context;
};

export default useCarRentalSearchBar;
